import React, { useRef, useState } from 'react';
import { Alert } from '@mui/material';

interface ImageUploadProps {
  currentImageUrl?: string;
  onUpload: (file: File) => Promise<string>;
  label: string;
  accept?: string;
  maxSizeMB?: number;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImageUrl,
  onUpload,
  label,
  accept = 'image/*',
  maxSizeMB = 5,
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (!file.type.startsWith('image/')) {
      return 'Apenas arquivos de imagem são permitidos.';
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `Arquivo muito grande. Máximo ${maxSizeMB}MB.`;
    }
    return null;
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setLoading(true);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);

    try {
      await onUpload(file);
    } catch (err: any) {
      setError(err.message || 'Erro ao fazer upload.');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const displayImage = preview || currentImageUrl;

  return (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      
      <div className="d-flex align-items-start gap-3">
        {displayImage && (
          <div className="position-relative">
            <img
              src={displayImage}
              alt="Preview"
              className="rounded"
              style={{
                width: '120px',
                height: '120px',
                objectFit: 'cover',
                border: '2px solid #dee2e6',
              }}
            />
            {loading && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 rounded"
              >
                <div className="spinner-border spinner-border-sm text-light" />
              </div>
            )}
          </div>
        )}
        
        <div className="flex-grow-1">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleClick}
            disabled={loading}
          >
            {loading ? 'Enviando...' : displayImage ? 'Alterar' : 'Selecionar'} {label}
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="d-none"
          />
          
          <div className="form-text mt-1">
            Formatos aceitos: JPG, PNG, GIF. Máximo {maxSizeMB}MB.
          </div>
        </div>
      </div>

      {error && (
        <Alert severity="error" className="mt-2">
          {error}
        </Alert>
      )}
    </div>
  );
};

export default ImageUpload;